/**
 * WatchlistPanel — 2026-06-07 n-perf-and-movers.
 *
 * Real user-scoped watchlist backed by /api/v1/user/watchlist.
 *
 * UX:
 *   - "Add Symbol" toggles an inline form (input + Add + Cancel).
 *   - On submit, POST /user/watchlist; refresh GET; show validation error
 *     inline if backend rejects.
 *   - Each row: Symbol, Price, Move %, Quote status badge, Remove button.
 *   - Empty state: "No symbols in your watchlist yet."
 *
 * Rules:
 *   - Never fabricates price / percent / company name / exchange.
 *   - No alert() popups.
 *   - Quote shows "—" + status badge when backend returns
 *     quote_unavailable.
 */
import React, { useEffect, useState } from 'react';
import SymbolChartButton from './SymbolChartButton';  // ADV-P0K
import { Plus, X, Trash2, Loader2 } from 'lucide-react';
import { apiV1Base } from '../lib/apiConfig';
import { EM_DASH, formatUsd, formatPercent, safeFormatTimestamp } from '../lib/accountFormatters';
import useLiveQuotes from '../lib/useLiveQuotes';
import { RefreshCw, ClipboardList } from 'lucide-react';
import { WATCHLIST_CHANGED_EVENT } from '../lib/watchlistActions'; // ADV-MKT-WATCHLIST-ADD-ACTION-001

interface WatchlistRow {
  symbol: string;
  quote: {
    price?: number | string | null;
    change?: number | string | null;
    change_pct?: number | string | null;
    volume?: number | string | null;
    timestamp?: string | null;
  } | null;
  status: 'quote_available' | 'quote_unavailable' | string;
  created_at_utc?: string;
}

function _token(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('adversyn_auth_token');
}

async function _fetchList(): Promise<WatchlistRow[] | { error: string }> {
  const token = _token();
  if (!token) return { error: 'unauthenticated' };
  try {
    const r = await fetch(apiV1Base() + '/user/watchlist', {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
    });
    if (!r.ok) return { error: `HTTP ${r.status}` };
    const body = await r.json();
    const data = body?.data ?? body;
    return Array.isArray(data?.symbols) ? data.symbols : [];
  } catch (e: any) {
    return { error: String(e?.message || e) };
  }
}

async function _addSymbol(symbol: string): Promise<{ ok: boolean; error?: string }> {
  const token = _token();
  if (!token) return { ok: false, error: 'unauthenticated' };
  try {
    const r = await fetch(apiV1Base() + '/user/watchlist', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ symbol }),
    });
    if (!r.ok) {
      let msg = `HTTP ${r.status}`;
      try {
        const body = await r.json();
        msg = body?.error?.message || msg;
      } catch { /* ignore */ }
      return { ok: false, error: msg };
    }
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: String(e?.message || e) };
  }
}

async function _removeSymbol(symbol: string): Promise<boolean> {
  const token = _token();
  if (!token) return false;
  try {
    const r = await fetch(
      apiV1Base() + '/user/watchlist/' + encodeURIComponent(symbol),
      { method: 'DELETE', headers: { Authorization: `Bearer ${token}` }, credentials: 'omit' },
    );
    return r.ok;
  } catch {
    return false;
  }
}

export default function WatchlistPanel() {
  const [rows, setRows] = useState<WatchlistRow[]>([]);
  // 2026-06-07 quote service - live quotes overlay
  const _watchSymbols = React.useMemo(() => rows.map((r) => r.symbol), [rows]);
  const liveQuotes = useLiveQuotes(_watchSymbols);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addValue, setAddValue] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    const res = await _fetchList();
    if (Array.isArray(res)) {
      setRows(res);
    } else {
      setError(res.error);
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  // ADV-MKT-WATCHLIST-ADD-ACTION-001 — refresh when a symbol is added from the
  // Markets board (or anywhere) so the Active Watchlist stays in sync.
  useEffect(() => {
    const h = () => { void refresh(); };
    window.addEventListener(WATCHLIST_CHANGED_EVENT, h);
    return () => window.removeEventListener(WATCHLIST_CHANGED_EVENT, h);
  }, []);

  const onSubmit = async () => {
    const sym = (addValue || '').trim().toUpperCase();
    if (!sym) { setAddError('Enter a symbol (e.g., AAPL).'); return; }
    if (!/^[A-Z0-9.\-]{1,10}$/.test(sym)) {
      setAddError('Symbol must be 1–10 chars; A–Z, 0–9, dot, hyphen.');
      return;
    }
    setSubmitting(true);
    setAddError(null);
    const res = await _addSymbol(sym);
    setSubmitting(false);
    if (!res.ok) {
      setAddError(res.error || 'Add failed.');
      return;
    }
    setAddValue('');
    setAddOpen(false);
    await refresh();
  };

  return (
    <div
      data-testid="watchlist-panel"
      className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">
          Active Watchlist
        </h3>
        <div className="flex items-center gap-3">
          <button
            data-testid="watchlist-refresh-quotes"
            onClick={() => { void liveQuotes.refresh(); }}
            disabled={liveQuotes.loading}
            className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-[var(--muted-text)] hover:text-emerald-600 disabled:opacity-50"
            title="Refresh quotes"
          >
            <RefreshCw size={12} className={liveQuotes.loading ? 'animate-spin' : ''} /> Refresh Quotes
          </button>
          <button
            data-testid="watchlist-add-toggle"
            onClick={() => { setAddOpen((v) => !v); setAddError(null); }}
            className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-700"
          >
            {addOpen ? <X size={14} /> : <Plus size={14} />} {addOpen ? 'Close' : 'Add Symbol'}
          </button>
        </div>
      </div>

      {addOpen && (
        <div data-testid="watchlist-add-form" className="mb-4 p-3 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)]">
          <div className="flex items-center gap-2">
            <input
              data-testid="watchlist-add-input"
              type="text"
              value={addValue}
              onChange={(e) => { setAddValue(e.target.value); setAddError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
              placeholder="AAPL, NVDA, SPY..."
              maxLength={10}
              className="flex-1 px-3 py-2 rounded-md border border-[var(--input-border)] bg-[var(--card-bg)] text-sm font-bold uppercase tracking-widest text-[var(--app-text)] outline-none focus:border-emerald-600"
            />
            <button
              data-testid="watchlist-add-submit"
              onClick={onSubmit}
              disabled={submitting}
              className="px-4 py-2 rounded-md bg-emerald-600 text-white text-xs font-black uppercase tracking-widest hover:bg-emerald-700 disabled:opacity-50"
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : 'Add'}
            </button>
            <button
              data-testid="watchlist-add-cancel"
              onClick={() => { setAddOpen(false); setAddValue(''); setAddError(null); }}
              className="px-3 py-2 rounded-md border border-[var(--input-border)] text-xs font-black uppercase tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)]"
            >
              Cancel
            </button>
          </div>
          {addError && (
            <div data-testid="watchlist-add-error" className="mt-2 text-xs font-bold text-rose-600">
              {addError}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div data-testid="watchlist-loading" className="py-6 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
          Loading watchlist…
        </div>
      )}

      {!loading && error && (
        <div data-testid="watchlist-error" className="py-6 text-center text-xs font-bold uppercase tracking-widest text-rose-600">
          Watchlist unavailable.
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div data-testid="watchlist-empty" className="py-6 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
          No symbols in your watchlist yet. Add a symbol to track it here.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div data-testid="watchlist-rows" className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--card-border)] text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] text-left">
                <th className="px-3 py-2">Symbol</th>
                <th className="px-3 py-2">Price</th>
                <th className="px-3 py-2">Move %</th>
                <th className="px-3 py-2">Quote</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // 2026-06-07 u-batch - canonical quote source ONLY.
                // Do NOT fall back to r.quote (mover-cached) - if the
                // live quote service has no data for this symbol, the
                // row honestly says "Quote unavailable".
                const live = liveQuotes.quotes[r.symbol] || null;
                const quality = (live?.data_quality as string | undefined) || 'unavailable';
                const isUnavail = quality === 'unavailable' || live?.price == null;
                const price = live?.price ?? null;
                const movePct = live?.change_pct ?? null;
                const moveDollars = live?.change ?? null;
                const lastUpdated = live?.quote_time_utc ?? null;
                const source = live?.source || null;
                const chip = (
                  quality === 'live' ? { label: 'Live', dot: 'bg-emerald-500', text: 'text-emerald-700' }
                  : quality === 'delayed' ? { label: 'Delayed', dot: 'bg-amber-500', text: 'text-amber-700' }
                  : quality === 'after_hours' ? { label: 'After hours', dot: 'bg-slate-400', text: 'text-slate-600' }
                  : quality === 'market_closed_latest' ? { label: 'Latest', dot: 'bg-slate-400', text: 'text-slate-600' }
                  : quality === 'closed' ? { label: 'Closed', dot: 'bg-slate-400', text: 'text-slate-600' }
                  : quality === 'stale' ? { label: 'Stale', dot: 'bg-amber-500', text: 'text-amber-700' }
                  : { label: 'Unavailable', dot: 'bg-rose-400', text: 'text-rose-600' }
                );
                const moveColor = (typeof movePct === 'number' ? (movePct >= 0 ? 'text-emerald-600' : 'text-rose-600') : 'text-[var(--muted-text)]');
                return (
                  <tr key={r.symbol} data-testid={`watchlist-row-${r.symbol}`} data-quote-quality={quality} className="border-b border-[var(--card-border)] hover:bg-[var(--input-bg)] transition-colors">
                    <td className="px-3 py-2 text-sm font-black text-[var(--app-text)] italic">
                      <div className="flex items-center gap-1.5">
                        <span>{r.symbol}</span>
                        <SymbolChartButton symbol={r.symbol} />
                        <button
                          type="button"
                          data-testid={`watchlist-plan-${r.symbol}`}
                          title="Plan paper session"
                          aria-label={`Plan paper session for ${r.symbol}`}
                          onClick={() => { try { window.dispatchEvent(new CustomEvent('adversyn:plan-paper-session', { detail: { symbol: r.symbol, price: (live && typeof live.price === 'number') ? live.price : null, volume: (live && typeof live.volume === 'number') ? live.volume : null, change_percent: (live && typeof live.change_pct === 'number') ? live.change_pct : null, __source_component: 'watchlist' } })); } catch { /* ignore */ } }}
                          className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-bg)]"
                        >
                          <ClipboardList size={11} />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm font-bold text-[var(--app-text)] tabular-nums">{isUnavail ? EM_DASH : formatUsd(price)}</td>
                    <td className={"px-3 py-2 text-sm font-bold tabular-nums " + moveColor}>
                      {isUnavail ? EM_DASH : (
                        <span>
                          {formatPercent(movePct)}
                          {typeof moveDollars === 'number' && (
                            <span className="ml-1 text-[10px] opacity-60">({moveDollars >= 0 ? '+' : ''}{formatUsd(moveDollars)})</span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="inline-flex items-center gap-1.5">
                        <span className={'w-1.5 h-1.5 rounded-full ' + chip.dot + (quality === 'live' ? ' animate-pulse' : '')} />
                        <span
                          data-testid={`watchlist-quote-status-${r.symbol}`}
                          className={'text-[9px] font-black uppercase tracking-widest ' + chip.text}
                        >
                          {chip.label}
                        </span>
                      </div>
                      <div data-testid={`watchlist-quote-time-${r.symbol}`} className="text-[8px] font-bold uppercase tracking-widest text-[var(--muted-text)] mt-0.5">
                        {lastUpdated ? safeFormatTimestamp(lastUpdated, { mode: 'time', fallback: '—', invalidFallback: '—' }) : '—'}
                        {source ? ' · ' + source.replace('alpaca-iex-snapshot', 'Alpaca IEX').replace(/^alpaca/i, 'Alpaca') : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        data-testid={`watchlist-remove-${r.symbol}`}
                        onClick={async () => {
                          const ok = await _removeSymbol(r.symbol);
                          if (ok) refresh();
                        }}
                        className="text-rose-600 hover:text-rose-700"
                        title="Remove from watchlist"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Source: /api/v1/user/watchlist + /api/v1/market-data/quotes
      </div>
    </div>
  );
}
